import { uniqueId } from "lodash";
import { useRef, useState } from "react";
import { BiCheck } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { Container, Draggable } from "react-smooth-dnd";
import SelectBox from "../../../../utils/components/SelectBox";
import FormField from "./FormField";
import RepeatableForm from "./RepeatableForm";
import formatFormTemplateField from "../../../../utils/helpers/formatFormTemplateField";

const DND_META_KEYS = ["source", "sourceRepeatableId", "repeatedFieldIndex"];

const itemFromPayload = (payload) => {
  const item = { ...payload };

  DND_META_KEYS.forEach((key) => delete item[key]);

  if (item.repeatedFields) {
    item.repeatedFields = item.repeatedFields.map((field) => ({ ...field }));
  }

  return item;
};

const SubForm = ({
  translate,
  form,
  index,
  setFormTemplate,
  formTemplate,
  selectedField,
  setSelectedField,
  isEditing,
  roles,
}) => {
  const fieldBodyRef = useRef(null);
  const getGhostParent = () => fieldBodyRef.current || document.body;

  const [isEditingSubFormName, setIsEditingSubFormName] = useState(false);
  const [newSubFormName, setNewSubFormName] = useState(form.name);

  const cloneStructure = (structure) =>
    structure.map((subForm, subFormIndex) =>
      subFormIndex === index
        ? {
            ...subForm,
            fields: subForm.fields.map((field) =>
              field.isRepeatable && field.repeatedFields
                ? { ...field, repeatedFields: [...field.repeatedFields] }
                : field
            ),
          }
        : subForm
    );

  const resolveContainer = (fields, targetRepeatableId) => {
    if (targetRepeatableId === null) return fields;

    const repeatable = fields.find((field) => field.id === targetRepeatableId);

    return repeatable ? repeatable.repeatedFields : null;
  };

  const createItemFromPayload = (
    payload,
    addedIndex,
    structure,
    isInsideRepeatable
  ) => {
    if (payload.source !== "sidebar") return itemFromPayload(payload);

    if (payload?.fieldType === "repeatable") {
      return {
        id: uniqueId(`repeatable-`),
        isRepeatable: true,
        repeatedFields: [],
      };
    }

    return itemFromPayload(
      formatFormTemplateField(payload, addedIndex, structure, isInsideRepeatable)
    );
  };

  const updateFieldIndexes = (fields) => {
    fields.forEach((field, i) => {
      field.fieldIndex = i;
      if (field.isRepeatable && field.repeatedFields) {
        field.repeatedFields.forEach((rf, j) => {
          if (rf) rf.repeatedFieldIndex = j;
        });
      }
    });
  };

  const onDrop = (dropResult, targetRepeatableId = null) => {
    const { removedIndex, addedIndex, payload } = dropResult;
    if ((removedIndex === null && addedIndex === null) || !payload) return;

    setFormTemplate((prevFormTemplate) => {
      const newFormTemplate = cloneStructure(prevFormTemplate);
      const fields = newFormTemplate[index].fields;
      const container = resolveContainer(fields, targetRepeatableId);

      if (!container) return prevFormTemplate;

      let removedItem = null;

      if (removedIndex !== null) {
        [removedItem] = container.splice(removedIndex, 1);
      }

      if (addedIndex !== null) {
        const itemToAdd =
          removedItem ||
          createItemFromPayload(
            payload,
            addedIndex,
            newFormTemplate,
            targetRepeatableId !== null
          );

        if (!itemToAdd) return prevFormTemplate;

        container.splice(addedIndex, 0, itemToAdd);
      }

      updateFieldIndexes(fields);

      return newFormTemplate;
    });
  };

  const handleSetSubFormRole = (role) => {
    const newFormTemplate = [...formTemplate];

    newFormTemplate[index].role = role;
    setFormTemplate(newFormTemplate);
  };

  const handleSetSubFormName = () => {
    const newFormTemplate = [...formTemplate];

    newFormTemplate[index].name = newSubFormName;
    setFormTemplate(newFormTemplate);
    setIsEditingSubFormName(false);
  };

  const handleRemoveSubForm = () => {
    const newFormTemplate = [...formTemplate];

    newFormTemplate.splice(index, 1);
    setFormTemplate(newFormTemplate);
  };

  return (
    <div className="sub-form-holder">
      <div className="sub-form-header d-flex justify-content-between align-items-center">
        <h3 className="sub-form-title text-uppercase d-flex justify-content-start align-items-center m-0">
          {isEditing && (
            <MdDelete
              className="fs-5 me-2 mb-1 cursor--pointer"
              onClick={handleRemoveSubForm}
            />
          )}
          <span>{translate("form_fields.sub_form")} - </span>
          <input
            type="text"
            name="name"
            value={newSubFormName}
            readOnly={!isEditingSubFormName || !isEditing}
            onDoubleClick={
              isEditing
                ? (e) => {
                    e.preventDefault();
                    setIsEditingSubFormName(true);
                  }
                : null
            }
            onChange={(e) => setNewSubFormName(e.target.value)}
          />
          {isEditingSubFormName && isEditing && (
            <span
              className="apply-button"
              onClick={handleSetSubFormName}
              role="button"
            >
              <BiCheck size="20px" />
            </span>
          )}
        </h3>
        <div className="sub-form-role d-flex align-items-center justify-content-end">
          <span>
            {translate("form_templates.template_structure.company_role")}:
          </span>
          <SelectBox
            options={roles}
            value={form.role}
            onChange={(role) => handleSetSubFormRole(role)}
            className="role-select ms-2 my-2"
            disabled={!isEditing}
          />
        </div>
      </div>
      <div className="forms-holder">
        <div className="form-field-holder">
          <div className="form-field-body" ref={fieldBodyRef}>
            <Container
              dragHandleSelector={
                isEditing ? ".drag-trigger" : ".no-drag-available"
              }
              lockAxis="y"
              dragClass="cursor--grabbing"
              getGhostParent={getGhostParent}
              shouldAcceptDrop={(sourceContainerOptions) => {
                return (
                  sourceContainerOptions.groupName.includes("group") ||
                  sourceContainerOptions.groupName.includes("field") ||
                  sourceContainerOptions.groupName.includes("repeatable")
                );
              }}
              groupName={`field-${index}`}
              onDrop={(dropResult) => onDrop(dropResult, null)}
              getChildPayload={(fieldIndex) => ({
                ...form.fields[fieldIndex],
                id: form.fields[fieldIndex].id,
                source: "form-field",
              })}
            >
              {form.fields.map((field, i) => (
                <Draggable key={field.id || i}>
                  {field.isRepeatable ? (
                    <RepeatableForm
                      translate={translate}
                      formIndex={index}
                      formTemplate={formTemplate}
                      setFormTemplate={setFormTemplate}
                      selectedField={selectedField}
                      setSelectedField={setSelectedField}
                      isEditing={isEditing}
                      form={form}
                      index={i}
                      field={field}
                      onDropInRepeatable={onDrop}
                      getGhostParent={getGhostParent}
                    />
                  ) : (
                    <FormField
                      translate={translate}
                      field={field}
                      fieldIndex={i}
                      formIndex={index}
                      selectedField={selectedField}
                      setSelectedField={setSelectedField}
                      isEditing={isEditing}
                      formTemplate={formTemplate}
                      setFormTemplate={setFormTemplate}
                    />
                  )}
                </Draggable>
              ))}
            </Container>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubForm;
